import { DarkTheme, DefaultTheme, ThemeProvider } from "@-/com.npmjs.expo-router/react-navigation";
import { AppLayoutContent } from "#app-layout-content";
import { useUniwind } from "@-/com.npmjs.uniwind";
import { AppLayoutSuspense } from "#app-layout-suspense";
import { AppLocalAccountGate } from "#app-local-account-gate";

/**
 * The app chrome — the global bottom tabs, account drawer and its trigger cluster — renders with NO
 * suspense boundary above it, so it commits on the first frame like a server-rendered shell.
 *
 * A `SuspensePageBoundary` used to wrap `AppLayoutContent` here. Because that content IS the
 * chrome, anything suspending anywhere in the shell subtree replaced navigation with a page
 * placeholder — so on a slow or absent connection the whole app became unnavigable, when the
 * correct degraded state is a working drawer over an empty scene. Content suspends inside the
 * destination scenes instead, where a stalled page leaves every other destination reachable.
 *
 * Keeping the chrome unconditional is also what lets the native boot shell be torn down on chrome
 * commit rather than on a screen's data: `ScreenReadyMarker` is allowed to never fire (an offline
 * device is a legitimate reason for content to suspend forever), so it can never gate a native
 * surface. See `bootsplash-app-mount-boundary`.
 *
 * `AppLayoutSuspense` stays above the chrome deliberately: it hosts `AppLayoutContextProvider` and
 * the ancestor DB providers the whole subtree reads, and it is non-suspending by construction.
 *
 * `AppLocalAccountGate` stays above everything: no app provider, query or screen mounts on a native
 * device without a local account.
 */
export function AppLayout() {
  const { theme: uniwindTheme } = useUniwind();
  const navigationTheme = uniwindTheme === "dark" ? DarkTheme : DefaultTheme;

  return (
    <AppLocalAccountGate>
      <ThemeProvider value={navigationTheme}>
        <AppLayoutSuspense>
          <AppLayoutContent key={uniwindTheme} />
        </AppLayoutSuspense>
      </ThemeProvider>
    </AppLocalAccountGate>
  );
}
